import FilesUpload from "components/EvtKit/file-upload/FilesUpload"
import IconCloseSmall from "components/EvtKit/icons/IconCloseSmall"
import IconImageUploadSmall from "components/EvtKit/icons/IconImageUploadSmall"
import Input from "components/EvtKit/input/Input"
import Caption from "components/Typography/Caption"
import IconButtonImageUpload from "./IconButtonImageUpload"

import React, { memo, useCallback, useMemo } from "react"
import styled from "styled-components"
import { useAppDispatch } from "reducers/store"
import { showToast } from "reducers/toast/toast-actions"
import { invalidFileFormat } from "../constants/defaultValues"

const Content = styled.div`
  width: 100%;
  display: flex;
  gap: 4px;
  align-items: flex-start;
  flex-direction: column;
`

const Container = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  gap: 8px;
`

const InputContainer = styled.div`
  width: 100%;
`

const FileNameContainer = styled.div`
  width: 100%;
  display: flex;
  gap: 4px;
  align-items: center;
`

interface UploadImageAsFileOrURLProps {
  id: string
  inputValue: string
  label: string
  name: string
  file?: File
  maxSize?: number
  isDisabled?: boolean
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  setFiles: React.Dispatch<React.SetStateAction<App.ProductFilesObj>>
  acceptFormats: string[]
  placeholder: string
}

const UploadImageAsFileOrURL = (props: UploadImageAsFileOrURLProps) => {
  const {
    maxSize = 48,
    id,
    inputValue,
    label,
    name,
    file,
    setFiles,
    handleInputChange,
    isDisabled = false,
    acceptFormats,
    placeholder
  } = props

  const appDispatch = useAppDispatch()

  const onFilesChange = useCallback(
    (files: File[]) => {
      if (files.length === 0) {
        appDispatch(showToast(invalidFileFormat, "error"))

        return
      }

      setFiles((prev) => ({
        ...prev,
        [name]: files[0]
      }))
    },
    [name]
  )

  const onRemoveFile = useCallback(() => {
    setFiles((prev) => ({
      ...prev,
      [name]: undefined
    }))
  }, [name])

  const truncatedFileName = useMemo(() => {
    if (file && file?.name.length > maxSize) {
      return `${file?.name.slice(0, maxSize)}...`
    }

    return file?.name
  }, [file?.name, maxSize])

  return (
    <Content>
      <Container>
        <InputContainer>
          <Input
            type="text"
            id={id}
            name={name}
            label={label}
            value={inputValue}
            className="fullWidth"
            onChange={handleInputChange}
            placeholder={placeholder}
            shrink
            disabled={!!file || isDisabled}
          />
        </InputContainer>

        <FilesUpload acceptFormats={acceptFormats} onFilesChange={onFilesChange}>
          <IconButtonImageUpload disabled={inputValue?.length > 0 || isDisabled} />
        </FilesUpload>
      </Container>

      {file && (
        <FileNameContainer>
          <IconImageUploadSmall />
          <Caption size="large" weight="light" color="primary-dark">
            {truncatedFileName}
          </Caption>
          <button onClick={onRemoveFile} type="button">
            <IconCloseSmall />
          </button>
        </FileNameContainer>
      )}
    </Content>
  )
}

export default memo(UploadImageAsFileOrURL)
